/**
 * AI 어시스턴트 커스텀 훅
 * 상태 관리 및 API 호출 분리
 */

import { useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { usePassword } from '../../contexts/PasswordContext';
import { useSecureNotes } from '../../contexts/SecureNotesContext';
import { useKnowledge } from '../../contexts/KnowledgeContext';
import { useItems } from '../../contexts/ItemsContext';

export function useAssistant() {
  const { passwords, addPassword } = usePassword();
  const { notes, createNote, updateNote, loading: notesLoading, loadNotes } = useSecureNotes();
  const { items: knowledgeItems } = useKnowledge();
  const { creditCards, apiKeys, loading: itemsLoading } = useItems();

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 메모 저장 헬퍼
  const saveToNote = useCallback(async (content) => {
    const today = new Date().toISOString().split('T')[0];
    const aiNoteTitle = `${today} AI 메모`;
    const timeStamp = new Date().toLocaleTimeString('ko-KR', {
      hour: '2-digit', minute: '2-digit'
    });
    const newEntry = `[${timeStamp}] ${content}`;

    const existingNote = notes.find(n => n.title === aiNoteTitle);

    if (existingNote) {
      const updatedContent = existingNote.plainTextContent
        ? `${existingNote.plainTextContent}\n${newEntry}`
        : newEntry;
      await updateNote(existingNote.id, {
        content: updatedContent,
        plainTextContent: updatedContent
      });
    } else {
      await createNote({
        title: aiNoteTitle,
        content: newEntry,
        plainTextContent: newEntry
      });
    }

    return aiNoteTitle;
  }, [notes, createNote, updateNote]);

  // 액션 처리
  const handleAction = useCallback(async (action) => {
    if (!action || action.confidence < 0.7) return null;

    try {
      if (action.intent === 'add_password' && action.data) {
        const { siteName, siteUrl, username, password } = action.data;

        if (siteName && username && password) {
          try {
            await addPassword({
              title: siteName,
              siteName,
              url: siteUrl || '',
              username,
              password
            });
            return { type: 'password', name: siteName };
          } catch (err) {
            const noteTitle = await saveToNote(
              `${siteName} 정보: ${username ? `아이디: ${username}` : ''} ${password ? `비밀번호: ${password}` : ''} ${siteUrl ? `URL: ${siteUrl}` : ''}`
            );
            return { type: 'note_fallback', name: noteTitle };
          }
        } else {
          const info = [];
          if (siteName) info.push(`사이트: ${siteName}`);
          if (username) info.push(`아이디: ${username}`);
          if (password) info.push(`비밀번호: ${password}`);
          if (siteUrl) info.push(`URL: ${siteUrl}`);

          if (info.length > 0) {
            const noteTitle = await saveToNote(info.join(', '));
            return { type: 'note', name: noteTitle };
          }
        }
      } else if (action.intent === 'add_note' && action.data) {
        const { content } = action.data;
        if (content) {
          const noteTitle = await saveToNote(content);
          return { type: 'note', name: noteTitle };
        }
      }
    } catch (err) {
      console.error('액션 처리 오류:', err);
    }
    return null;
  }, [addPassword, saveToNote]);

  // 데이터 준비 (accepts optional fresh notes for immediate use after reload)
  const prepareData = useCallback((freshNotes = null) => {
    const passwordList = passwords.map(p => ({
      id: p.id,
      siteName: p.siteName || p.title,
      siteUrl: p.url,
      username: p.username,
      password: p.password || null
    }));

    // 메모 목록 (전체 내용 포함 - 검색 정확도 향상)
    // Use freshNotes if provided (after reload), otherwise use context notes
    const notesToUse = freshNotes || notes;
    const noteList = notesToUse.map(n => ({
      id: n.id,
      title: n.title,
      plainTextContent: n.plainTextContent || n.content || ''
    }));

    console.log('📝 검색 대상 메모:', noteList.length, '개');

    const knowledgeList = knowledgeItems.map(k => ({
      id: k.id,
      title: k.title,
      content: k.content,
      tags: k.tags
    }));

    const creditCardList = (creditCards || []).map(c => ({
      id: c.id,
      cardName: c.cardName || c.title,
      cardNumber: c.cardNumber,
      cardHolder: c.cardHolder,
      expiryDate: c.expiryMonth && c.expiryYear
        ? `${c.expiryMonth}/${c.expiryYear}`
        : (c.expiryDate || ''),
      cvv: c.cvv
    }));

    const apiKeyList = (apiKeys || []).map(a => ({
      id: a.id,
      serviceName: a.serviceName || a.title,
      apiKey: a.apiKey,
      description: a.description
    }));

    return { passwordList, noteList, knowledgeList, creditCardList, apiKeyList };
  }, [passwords, notes, knowledgeItems, creditCards, apiKeys]);

  // 메시지 전송
  const sendMessage = useCallback(async (userMessage) => {
    if (!userMessage.trim() || isLoading) return;

    setError(null);

    // 사용자 메시지 추가
    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      // 메모가 로드되지 않았으면 새로고침하고 바로 사용
      let freshNotes = null;
      if (notes.length === 0 && loadNotes) {
        console.log('📝 메모 새로고침 중...');
        freshNotes = await loadNotes();
        console.log('📝 새로고침 완료:', freshNotes?.length || 0, '개');
      }

      const data = prepareData(freshNotes);
      console.log('🔍 검색 데이터:', {
        passwords: data.passwordList.length,
        notes: data.noteList.length,
        creditCards: data.creditCardList.length,
        apiKeys: data.apiKeyList.length
      });

      const functions = getFunctions();
      const askAssistant = httpsCallable(functions, 'askAssistant');

      const result = await askAssistant({
        question: userMessage,
        conversationHistory: messages.slice(-10),
        ...data
      });

      const { answer, action, sources } = result.data;

      // AI 응답 추가
      const assistantMsg = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: answer,
        timestamp: new Date(),
        sources: sources || [],
        action
      };
      setMessages(prev => [...prev, assistantMsg]);

      // 액션 처리
      const actionResult = await handleAction(action);
      if (actionResult) {
        const systemMsg = {
          id: (Date.now() + 2).toString(),
          role: 'system',
          content: actionResult.type === 'password'
            ? `"${actionResult.name}" 패스워드를 저장했습니다.`
            : actionResult.type === 'note_fallback'
            ? `패스워드 저장 실패, "${actionResult.name}"에 메모로 저장했습니다.`
            : `"${actionResult.name}"에 메모로 저장했습니다.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, systemMsg]);
      }

    } catch (err) {
      console.error('AI 어시스턴트 오류:', err);
      setError(err.message);

      const errorMsg = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `죄송합니다, 오류가 발생했습니다: ${err.message}`,
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, messages, prepareData, handleAction, notes, loadNotes]);

  // 대화 초기화
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages
  };
}
